import { useTranslation } from 'react-i18next';
import { Box } from 'reflexbox';
import styled from 'styled-components';

import { Redirect, Route, Switch } from '@treams/treams-routes';
import { Jumbo, Tabs } from '@treams/treams-ui';

import * as graphql from 'generated/graphql';
import CurrentlyViewedUserContext from 'packages/kakapo-ui-application/contexts/CurrentlyViewedUserContext';
import useAuthenticatedUser from 'packages/kakapo-ui-application/hooks/useAuthenticatedUser';
import { JourneyListContainerWithoutBackground } from 'packages/kakapo-ui-application/pages/journey/JourneyListContainer';
import LearningEventModalID from '../packages/kakapo-ui-application/pages/learning/pages/LearningEventModal';
import Jobs from '../job/Jobs';

const Container = styled(Box)`
	background: ${(props) => props.theme.colors.whiteBlue};
`;

export default function Journey(props: { userID?: string; jumbo?: boolean }) {
	const { t } = useTranslation();

	const viewer = useAuthenticatedUser();
	const query = graphql.useUserQuery({ variables: { id: props.userID ?? viewer.id } });

	if (!query.data) {
		return null;
	}

	return (
		<>
			{props.jumbo !== false && (
				<Jumbo
					title={`${query.data.user.firstName} ${query.data.user.lastName}`}
					wide={1440}
					footer={
						<Tabs.Container wide={1440}>
							{query.data.user.viewerCanViewJourney && (
								<Tabs.Item
									path={props.userID ? `/users/${props.userID}/journey` : `/journey`}
									title={t('journey.tabs.journey')}
								/>
							)}
							<Tabs.Item
								path={props.userID ? `/users/${props.userID}/journey/jobs` : `/journey/jobs`}
								title={t('journey.tabs.jobs')}
							/>
						</Tabs.Container>
					}
				/>
			)}
			<Switch>
				<Route
					path={[
						'/users/:id/journey',
						'/journey',
						'/users/:userID/journey/learning/:ID',
						'/journey/learning/:id',
					]}
					exact
				>
					{query.data.user.viewerCanViewJourney ? (
						<Container py={5}>
							<CurrentlyViewedUserContext.Provider
								value={{
									currentlyViewedUser: query.data.user as graphql.User,
								}}
							>
								<Box maxWidth={1200}>
									<JourneyListContainerWithoutBackground user={query.data.user as graphql.User} />
								</Box>
							</CurrentlyViewedUserContext.Provider>

							<Route<{ id: string }>
								path={['/users/:userID/journey/learning/:ID', '/journey/learning/:id']}
							>
								{({ match }) =>
									match && (
										<LearningEventModalID
											eventID={match.params.id!}
											returnURL={props.userID ? `/users/${props.userID}/journey` : '/journey'}
										/>
									)
								}
							</Route>
						</Container>
					) : (
						<Redirect to={`/users/${query.data.user.id}/journey/jobs`} />
					)}
				</Route>
				<Route path={['/users/:id/journey/jobs', '/journey/jobs']}>
					<Container px={5} py={4}>
						<Jobs userID={query.data.user.id} />
					</Container>
				</Route>
			</Switch>
		</>
	);
}
